/* =====================================================================
 * Agent API Client — SSE 스트리밍 + 일반 요청 지원
 * ===================================================================== */

import type {
  AgentRequest,
  AgentStreamResponse,
  AgentCompletedResponse,
  AgentFailedResponse,
  HitlAnswer,
  HitlResponseAcceptedDetail,
} from '@auw/workspace-utils';
import {
  authFetch,
  clearCachedCsrfToken,
  ensureCsrfToken,
  ensureFreshToken,
  getHeaders,
  API_BASE_URL,
  hasNativeSessionAdapter,
  clearNativeSession,
} from './authFetch';
import { getActiveTenantManifest } from '@auw/tenant';

interface ExchangeAuthCodeResponse {
  access_token: string;
  expires_in: number;
  is_new_user: boolean;
}

interface DesktopExchangeResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  is_new_user: boolean;
}

const exchangeCodeInFlight = new Map<string, Promise<ExchangeAuthCodeResponse>>();
const exchangeCodeResolved = new Map<string, ExchangeAuthCodeResponse>();

/** 일회성 코드를 access_token으로 교환 (refresh는 HttpOnly cookie 발급) */
export async function exchangeAuthCode(code: string): Promise<ExchangeAuthCodeResponse> {
  const normalizedCode = String(code || '').trim();
  if (!normalizedCode) {
    throw new Error('Auth code exchange failed: invalid code');
  }

  const cached = exchangeCodeResolved.get(normalizedCode);
  if (cached) return cached;

  const inFlight = exchangeCodeInFlight.get(normalizedCode);
  if (inFlight) return inFlight;

  const url = `${API_BASE_URL}/api/v1/auth/exchange`;
  const task = (async () => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code: normalizedCode }),
    });
    if (!res.ok) {
      throw new Error(`Auth code exchange failed: ${res.status}`);
    }
    const data = await res.json() as ExchangeAuthCodeResponse;
    exchangeCodeResolved.set(normalizedCode, data);
    return data;
  })();

  exchangeCodeInFlight.set(normalizedCode, task);
  try {
    return await task;
  } finally {
    exchangeCodeInFlight.delete(normalizedCode);
  }
}

/** 데스크톱 앱용: 코드를 교환하면서 refresh_token도 body로 받음 */
export async function exchangeAuthCodeForDesktop(code: string): Promise<DesktopExchangeResponse> {
  const normalizedCode = String(code || '').trim();
  if (!normalizedCode) {
    throw new Error('Auth code exchange failed: invalid code');
  }

  const url = `${API_BASE_URL}/api/v1/auth/exchange`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: normalizedCode, desktop: true }),
  });
  if (!res.ok) {
    throw new Error(`Auth code exchange failed: ${res.status}`);
  }
  return await res.json() as DesktopExchangeResponse;
}

export async function logoutCurrentSession(options?: {
  keepalive?: boolean;
}): Promise<void> {
  if (hasNativeSessionAdapter()) {
    // Capture the current authorization before the UI clears its vault.
    const revoke = fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
      method: 'POST', headers: getHeaders(false), signal: AbortSignal.timeout(10_000),
    }).then(async (response) => { await response.body?.cancel(); }).catch(() => {});
    await clearNativeSession();
    await revoke;
    return;
  }
  const keepalive = Boolean(options?.keepalive);
  const revokeUrl = `${API_BASE_URL}/api/v1/auth/logout`;
  const clearCookieUrl = `${API_BASE_URL}/api/v1/auth/logout/cookie`;
  const csrfToken = await ensureCsrfToken();
  const csrfHeaders: Record<string, string> = csrfToken ? { 'X-CSRF-Token': csrfToken } : {};

  const revokeResPromise = fetch(revokeUrl, {
    method: 'POST',
    headers: {
      ...getHeaders(false),
      ...csrfHeaders,
    },
    credentials: 'include',
    keepalive,
  }).catch(() => null);

  // 인증 헤더가 만료/유실된 경우에도 refresh cookie는 반드시 삭제하도록 분리 호출
  const clearCookieResPromise = fetch(clearCookieUrl, {
    method: 'POST',
    headers: csrfHeaders,
    credentials: 'include',
    keepalive,
  }).catch(() => null);

  const [revokeRes, clearCookieRes] = await Promise.all([revokeResPromise, clearCookieResPromise]);
  const revokeOk = revokeRes ? (revokeRes.ok || revokeRes.status === 401) : false;
  const cookieCleared = Boolean(clearCookieRes?.ok);

  if (revokeOk || cookieCleared) {
    clearCachedCsrfToken();
    return;
  }
  throw new Error('Logout failed: unable to clear server session and refresh cookie.');
}

/* ------------------------------------------------------------------ */
/* SSE Streaming                                                       */
/* ------------------------------------------------------------------ */

export const AGENT_STREAM_TERMINAL_MISSING =
  'AGENT_STREAM_TERMINAL_MISSING' as const;

export class AgentStreamEndedWithoutTerminalError extends Error {
  readonly code = AGENT_STREAM_TERMINAL_MISSING;
  readonly workspaceType: string;
  readonly itemId: string | null;
  readonly messageId: string | null;

  constructor(request: AgentRequest) {
    super('Agent stream ended before a terminal response was received.');
    this.name = 'AgentStreamEndedWithoutTerminalError';
    this.workspaceType = request.workspace_type || 'agent';
    this.itemId = request.item_id ?? null;
    this.messageId = request.message_id ?? null;
  }
}

export function isAgentStreamEndedWithoutTerminalError(
  value: unknown,
): value is AgentStreamEndedWithoutTerminalError {
  return (
    value instanceof AgentStreamEndedWithoutTerminalError
    || (
      typeof value === 'object'
      && value !== null
      && 'code' in value
      && value.code === AGENT_STREAM_TERMINAL_MISSING
    )
  );
}

function parseAgentSseEvent(eventBlock: string): AgentStreamResponse | null {
  const data = eventBlock
    .split(/\r\n|\r|\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n')
    .trim();
  if (!data) return null;
  try {
    return JSON.parse(data) as AgentStreamResponse;
  } catch {
    console.warn('[SSE] JSON parse error', data.slice(0, 200));
    return null;
  }
}

export async function* streamAgent(
  request: AgentRequest,
  signal?: AbortSignal,
): AsyncGenerator<AgentStreamResponse> {
  /* SSE 는 한 번 연결되면 응답 chunk 단위로 흐르기 때문에 그 동안에는
   * 401-refresh-retry 사이클이 (정상적으로는) 끼어들 여지가 없다.
   * 만료 직전 토큰으로 진입하면 backend 가 첫 frame 직전에 401 을
   * 던지고 ``authFetch`` 가 retry 하긴 하지만:
   *
   *   1. ``streamAgent`` 호출자가 ``getHeaders()`` 로 만료 토큰을
   *      ``init.headers`` 에 박은 채로 진입 → backend ``ExpiredSignature
   *      Error`` → 401.
   *   2. ``authFetch`` 가 ``/auth/refresh`` 로 새 토큰을 받아도 retry
   *      트래픽 (sync queue, stats 등) 과 경합 → backend 가 ``invalid``
   *      판정 → ``clear_refresh_cookie`` 호출되어 refresh cookie 가
   *      날아갈 수 있다 (Samsung onprem 에서 실증).
   *
   * ``ensureFreshToken()`` 을 선제 호출해 60s 버퍼로 미리 회전하면
   * 위 race window 자체가 사라진다. 이 패턴은 ``workspaceSSE.ts``,
   * ``collabWebSocket.ts``, ``useDMWebSocket.ts`` 가 이미 채택 — 같은
   * fetch-SSE 클라이언트인 ``streamAgent`` 만 빠져 있던 회귀를 닫는다. */
  await ensureFreshToken();

  const workspaceType = request.workspace_type || 'agent';
  const url = `${API_BASE_URL}/api/v1/workspaces/${workspaceType}/run/stream`;

  const res = await authFetch(url, {
    method: 'POST',
    headers: {
      ...getHeaders(),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ ...request, stream: true }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const eventBlocks = buffer.split(/\r\n\r\n|\r\r|\n\n/);
    buffer = eventBlocks.pop() ?? '';

    for (const eventBlock of eventBlocks) {
      const parsed = parseAgentSseEvent(eventBlock);
      if (!parsed) continue;
      yield parsed;
      if (parsed.status === 'completed' || parsed.status === 'failed') return;
    }
  }

  // EOF is an SSE event boundary. Accept an unterminated final terminal event,
  // but never interpret a clean transport close itself as successful completion.
  const finalEvent = parseAgentSseEvent(buffer);
  if (finalEvent) {
    yield finalEvent;
    if (finalEvent.status === 'completed' || finalEvent.status === 'failed') return;
  }
  if (signal?.aborted) return;
  throw new AgentStreamEndedWithoutTerminalError(request);
}

/* ------------------------------------------------------------------ */
/* Non-streaming                                                       */
/* ------------------------------------------------------------------ */

export async function runAgent(
  request: AgentRequest,
): Promise<AgentCompletedResponse | AgentFailedResponse> {
  /* streamAgent 와 동일한 회귀 방지 — 비-스트리밍 agent 호출도 같은
   * 만료-경합 시나리오를 그대로 받는다. */
  await ensureFreshToken();

  const workspaceType = request.workspace_type || 'agent';
  const url = `${API_BASE_URL}/api/v1/workspaces/${workspaceType}/run`;

  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ ...request, stream: false }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  return res.json();
}

/* ------------------------------------------------------------------ */
/* Stream Cancel (fire-and-forget)                                     */
/* ------------------------------------------------------------------ */

export async function submitAgentToolApproval(params: {
  workspaceType: string;
  sessionId: string;
  messageId: string;
  answer: HitlAnswer;
}): Promise<HitlResponseAcceptedDetail> {
  const res = await authFetch(`${API_BASE_URL}/api/v1/workspaces/${params.workspaceType}/run/approval`, {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ session_id: params.sessionId, message_id: params.messageId, hitl_response: params.answer }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(typeof body.detail === 'string' ? body.detail : '승인 응답을 전달하지 못했습니다. 다시 시도해 주세요.');
  }
  const receipt = await res.json();
  if (receipt.schema !== 'hitl-response-accepted/v1' || receipt.request_id !== params.answer.request_id
      || receipt.continuation_id !== params.answer.continuation_id) {
    throw new Error('승인 응답의 실행 정보가 일치하지 않습니다.');
  }
  return receipt;
}

export async function cancelAgentStream(params: {
  sessionId?: string;
  messageId?: string;
  workspaceType?: string;
}): Promise<void> {
  if (!params.sessionId && !params.messageId) return;

  const wsType = params.workspaceType || 'agent';
  const url = `${API_BASE_URL}/api/v1/workspaces/${wsType}/run/cancel`;

  try {
    await authFetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        session_id: params.sessionId ?? null,
        message_id: params.messageId ?? null,
      }),
    });
  } catch {
    // best-effort: swallow network errors
  }
}

/* ------------------------------------------------------------------ */
/* File Upload                                                         */
/* ------------------------------------------------------------------ */

export interface UploadedFileInfo {
  path: string;
  display_name: string;
  mime_type: string;
  file_id?: string;
  storage?: {
    kind: 'local_path' | 's3_object' | 'backend_download' | 'presigned_url';
    local_path?: string | null;
    bucket?: string | null;
    key?: string | null;
    region?: string | null;
    download_path?: string | null;
    presigned_url?: string | null;
    expires_at?: string | null;
  } | null;
  source?: {
    kind: 'local_path' | 's3_object' | 'backend_download' | 'presigned_url';
    local_path?: string | null;
    bucket?: string | null;
    key?: string | null;
    region?: string | null;
    download_path?: string | null;
    presigned_url?: string | null;
    expires_at?: string | null;
  } | null;
}

export interface UploadResult {
  uploaded: string[];
  uploaded_files?: Array<{
    file_id: string;
    name: string;
    mime_type: string;
    size: number;
  }>;
  uploaded_files_info?: UploadedFileInfo[];
  count: number;
  session_id: string;
  message_id: string;
  workspace_type?: string;
  errors?: Array<{ file: string; error: string }> | null;
}

export async function uploadFiles(
  files: File[],
  _userId?: string,
  sessionId?: string,
  messageId?: string,
  workspaceType: string = 'agent',
  itemId?: string,
  /**
   * 업로드를 중단할 신호 (선택).
   *
   * 큰 파일을 올리는 도중 사용자가 화면을 떠나면, 신호가 없으면 업로드가 끝까지
   * 돌아 취소한 파일이 저장소에 남는다. 기존 호출자는 넘기지 않으므로 거동이 같다.
   */
  signal?: AbortSignal,
): Promise<UploadResult> {
  const url = `${API_BASE_URL}/api/v1/workspaces/${workspaceType}/upload`;
  const form = new FormData();
  if (sessionId) form.append('session_id', sessionId);
  if (messageId) form.append('message_id', messageId);
  if (itemId) form.append('item_id', itemId);
  for (const f of files) {
    form.append('files', f);
  }

  const headers = getHeaders(false);
  // Note: Content-Type is NOT set — browser auto-sets multipart/form-data with boundary

  const res = await authFetch(url, {
    method: 'POST',
    headers,
    body: form,
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Upload failed (${res.status}): ${text}`);
  }

  return res.json();
}

/* ------------------------------------------------------------------ */
/* Health                                                              */
/* ------------------------------------------------------------------ */

export async function healthCheck(workspaceType: string = 'agent'): Promise<Record<string, unknown>> {
  const url = `${API_BASE_URL}/api/v1/workspaces/${workspaceType}/health`;
  const res = await authFetch(url, { headers: getHeaders() });
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Agents List                                                         */
/* ------------------------------------------------------------------ */

export async function listAgents(workspaceType: string = 'agent'): Promise<Record<string, unknown>> {
  const url = `${API_BASE_URL}/api/v1/workspaces/${workspaceType}/agents`;
  const res = await authFetch(url, { headers: getHeaders() });
  return res.json();
}

/* ------------------------------------------------------------------ */
/* File Download                                                       */
/* ------------------------------------------------------------------ */

export async function downloadAgentFile(params: {
  userId: string;
  sessionId: string;
  messageId: string;
  fileId: string;
  workspaceType?: string;
}): Promise<Blob> {
  const wsType = params.workspaceType || 'agent';
  const url = `${API_BASE_URL}/api/v1/workspaces/${wsType}/files/download`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      user_id: params.userId,
      session_id: params.sessionId,
      message_id: params.messageId,
      file_id: params.fileId,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Download failed (${res.status}): ${text}`);
  }
  return res.blob();
}

export async function previewAgentFile(params: {
  userId: string;
  sessionId: string;
  messageId: string;
  fileId: string;
  workspaceType?: string;
}): Promise<Blob> {
  const wsType = params.workspaceType || 'agent';
  const url = `${API_BASE_URL}/api/v1/workspaces/${wsType}/files/preview`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      user_id: params.userId,
      session_id: params.sessionId,
      message_id: params.messageId,
      file_id: params.fileId,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Preview failed (${res.status}): ${text}`);
  }
  return res.blob();
}

export interface BookmarkPreviewResult {
  url: string;
  resolved_url?: string | null;
  title: string | null;
  description: string | null;
  image: string | null;
  favicon: string | null;
  error?: string | null;
}

export async function previewBookmark(
  url: string,
  workspaceType: string = 'document',
): Promise<BookmarkPreviewResult> {
  const endpoint = `${API_BASE_URL}/api/v1/workspaces/${workspaceType}/bookmarks/preview`;
  const res = await authFetch(endpoint, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Bookmark preview failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Plugins                                                             */
/* ------------------------------------------------------------------ */

export interface PluginInfo {
  id: string;
  name: string;
  description: string;
  configured: boolean;
  connected: boolean;
  oauth_configured?: boolean;
  auth_scheme?: string | null;
  supported_auth_schemes?: string[];
  api_key_fields?: Array<{
    key: string;
    label: string;
    placeholder: string;
    secret: boolean;
    required: boolean;
  }>;
  transport: string | null;
  tool_count: number;
  tools: Array<{ name: string; description: string }>;
  team_name?: string | null;
  team_id?: string | null;
  token_hint?: string | null;
  send_as?: string | null;       // "bot" | "user"
  has_user_token?: boolean;
  error: string | null;
reauth_required?: boolean;

}

export interface PluginsListResponse {
  plugins: PluginInfo[];
  total: number;
  user_id: string;
}

export interface PluginVerifyResponse {
  success: boolean;
  transport: string;
  tool_count: number;
  tools: Array<{ name: string; description: string }>;
  error: string | null;
}

/* ---- Slack OAuth Types ---- */

export interface OAuthAuthorizeResponse {
  authorization_url: string;
  state: string;
}

export interface OAuthCallbackResponse {
  success: boolean;
  plugin_id: string;
  team_name?: string | null;
  team_id?: string | null;
  scope?: string | null;
  error?: string | null;
}

export interface SlackConnectionStatus {
  plugin_id: string;
  user_id: string;
  oauth_configured: boolean;
  connected: boolean;
  team_id?: string | null;
  team_name?: string | null;
  scope?: string | null;
  bot_user_id?: string | null;
  connected_at?: string | null;
  token_hint?: string | null;
}

export interface SlackTestResult {
  success: boolean;
  plugin_id: string;
  team?: string;
  team_id?: string;
  user?: string;
  user_id?: string;
  bot_id?: string;
  error?: string | null;
}

export interface PluginDisconnectResponse {
  success: boolean;
  plugin_id: string;
  message: string;
}

export interface ApiKeyConnectResponse {
  success: boolean;
  provider: string;
  message: string;
}

export interface AuthMeResponse {
  user_id: string;
  email?: string | null;
  name?: string | null;
  picture?: string | null;
  has_custom_picture?: boolean;
  company?: string | null;
  department?: string | null;
  tenant_identity?: Record<string, string | number | boolean | null> | null;
  preferred_language?: string | null;
  country_code?: string | null;
  timezone?: string | null;
  agent_pictures?: Record<string, string> | null;
  sidebar_favorite_modes?: string[] | null;
  tools_favorite_ids?: string[] | null;
  job_role?: string | null;
  interests?: string[] | null;
  custom_interests?: string[] | null;
  feed_delivery_hour?: number | null;
  feed_delivery_hour_locked_until?: string | null;
  onboarding_completed?: boolean | null;
  email_verified: boolean;
  /* 플랜 / 역할 / 사용량 (백엔드 확장 후 반영) */
  role?: string | null;
  plan?: string | null;
  tokens_used?: number | null;
  tokens_limit?: number | null;
  storage_used_bytes?: number | null;
  storage_limit_bytes?: number | null;
  consents?: ConsentsMap | null;
}

export interface KnoxSsoRequest {
  rpcode: string;
  encryptedUserInfo?: string;
  encryptedKey?: string;
  detail?: string | null;
}

export interface KnoxSsoResponse {
  success: boolean;
  message: string;
  data: {
    accessToken: string;
    userId: string;
    userGroupId: number;
    groupName: string;
    name: string;
    deptName?: string | null;
    nickName?: string | null;
    fileUrl?: string | null;
    employeeNo: string;
    serviceName: string;
    role: string;
  };
}

export async function postKnoxSso(
  payload: KnoxSsoRequest,
): Promise<KnoxSsoResponse> {
  const url = `${API_BASE_URL}/api/v1/knox/sso`;
  const tenant = getActiveTenantManifest();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-AUW-Tenant-Id': tenant.id,
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Knox SSO failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 전체 플러그인 목록 + 상태 조회 */
export async function listPlugins(): Promise<PluginsListResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Plugin list failed (${res.status}): ${text}`);
  }
  const data: PluginsListResponse = await res.json();
  const hiddenIds = new Set(getActiveTenantManifest().plugins?.hiddenIds ?? []);
  const plugins = data.plugins.filter((plugin) => !hiddenIds.has(plugin.id));
  return { ...data, plugins, total: plugins.length };
}

/** Slack 플러그인 상태 조회 */
export async function getSlackStatus(): Promise<PluginInfo> {
  const url = `${API_BASE_URL}/api/v1/plugins/slack/status`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Slack 연결 검증 (MCP 서버 실제 연결 테스트) */
export async function verifySlack(): Promise<PluginVerifyResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/slack/verify`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack verify failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Slack OAuth                                                         */
/* ------------------------------------------------------------------ */

/** Slack OAuth 인증 URL 생성 */
export async function getSlackOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/slack/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Slack OAuth 콜백 처리 (프론트에서 code/state 직접 전달) */
export async function slackOAuthCallback(
  code: string,
  state: string,
): Promise<OAuthCallbackResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/slack/oauth/callback`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack OAuth callback failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Slack OAuth 연결 상태 조회 */
export async function getSlackOAuthStatus(
  _userId?: string,
): Promise<SlackConnectionStatus> {
  const url = `${API_BASE_URL}/api/v1/plugins/slack/status`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack OAuth status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Slack 연결 테스트 (저장된 토큰으로 auth.test) */
export async function testSlackConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/slack/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Slack 연결 해제 */
export async function disconnectSlack(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/slack/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface SlackChannelItem {
  id: string;
  name: string;
  is_private?: boolean;
  member_count?: number;
}

export interface SlackChannelsResponse {
  channels: SlackChannelItem[];
  nextCursor: string | null;
  total: number;
}

export interface SlackChannelFileItem {
  id: string;
  name: string;
  mimeType?: string;
  size?: number;
  createdAt?: number;
  channelId: string;
}

export interface SlackChannelFilesResponse {
  files: SlackChannelFileItem[];
  nextPageToken: string | null;
  total: number;
}

export async function listSlackChannels(
  limit: number = 50,
  cursor?: string,
  query?: string,
): Promise<SlackChannelsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (query) params.set('query', query);
  const url = `${API_BASE_URL}/api/v1/plugins/slack/channels?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack channels failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listSlackChannelFiles(
  channelId: string,
  page: number = 1,
  limit: number = 50,
  query?: string,
): Promise<SlackChannelFilesResponse> {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (query) params.set('query', query);
  const url = `${API_BASE_URL}/api/v1/plugins/slack/channels/${encodeURIComponent(channelId)}/files?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack channel files failed (${res.status}): ${text}`);
  }
  return res.json();
}

export function getSlackFileDownloadUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/slack/files/${encodeURIComponent(fileId)}/download`;
}

export function getSlackFileThumbnailUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/slack/files/${encodeURIComponent(fileId)}/thumbnail`;
}

export async function downloadSlackFile(
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getSlackFileDownloadUrl(fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack file download failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

export async function downloadSlackFileThumbnail(
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getSlackFileThumbnailUrl(fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Slack file thumbnail failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'image/*' });
}

/** Notion 플러그인 상태 조회 */
export async function getNotionStatus(): Promise<PluginInfo> {
  const url = `${API_BASE_URL}/api/v1/plugins/notion/status`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Notion 연결 검증 (MCP 서버 실제 연결 테스트) */
export async function verifyNotion(): Promise<PluginVerifyResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/notion/verify`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion verify failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Notion MCP OAuth 인증 URL 생성 (Agent 도구용) */
export async function getNotionOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/notion/mcp-oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Notion Integration OAuth 인증 URL 생성 (파일 탐색/트리/페이지용) */
export async function getNotionIntegrationOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/notion/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion Integration OAuth failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Notion 연결 테스트 (저장된 토큰으로 users/me) */
export async function testNotionConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/notion/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Notion 연결 해제 */
export async function disconnectNotion(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/notion/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Atlassian (Jira + Confluence)                                       */
/* ------------------------------------------------------------------ */

/** Atlassian OAuth 인증 URL 생성 */
export async function getAtlassianOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Atlassian OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Atlassian 연결 테스트 */
export async function testAtlassianConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Atlassian test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Atlassian 연결 해제 */
export async function disconnectAtlassian(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Atlassian disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Collaboration Tools (Confluence / Notion)                          */
/* ------------------------------------------------------------------ */

export type CollaborationProvider = 'confluence' | 'notion';
export type CollaborationTreeParentType = 'root' | 'space' | 'page' | 'database';

export interface CollaborationPageItem {
  id: string;
  title: string;
  snippet?: string;
  space_name?: string;
  url?: string;
  last_edited_at?: string;
}

export interface CollaborationListResponse {
  pages: CollaborationPageItem[];
  nextPageToken: string | null;
}

export interface CollaborationPageDetail {
  id: string;
  title: string;
  url?: string;
  space_name?: string;
  last_edited_at?: string;
  content_text: string;
  content_html?: string;
}

export interface CollaborationTreeNode {
  id: string;
  provider: CollaborationProvider;
  node_type: 'space' | 'database' | 'page';
  title: string;
  parent_id?: string | null;
  has_children: boolean;
  subtitle?: string;
  url?: string;
  last_edited_at?: string;
  space_id?: string;
  space_key?: string;
}

export interface CollaborationTreeResponse {
  nodes: CollaborationTreeNode[];
  nextPageToken: string | null;
}

export interface CollaborationAttachmentItem {
  id: string;
  name: string;
  media_type?: string;
  size?: number | null;
  created_at?: string;
  download_url?: string;
  expiry_time?: string;
  source_type?: string;
}

export interface CollaborationAttachmentsResponse {
  attachments: CollaborationAttachmentItem[];
  nextPageToken: string | null;
}

export interface CollaborationAttachmentOpenTarget {
  url: string;
  revokeAfterMs?: number;
}

export type CollaborationApiRequestOptions = {
  signal?: AbortSignal;
};

function _normalizeCollaborationOpenUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
  return url;
}

async function _blobToOpenTarget(res: Response): Promise<CollaborationAttachmentOpenTarget> {
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    revokeAfterMs: 60_000,
  };
}

export async function listConfluenceTreeNodes(
  parentType: 'root' | 'space' | 'page',
  parentId?: string,
  limit: number = 30,
  pageToken?: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationTreeResponse> {
  const params = new URLSearchParams();
  params.set('parent_type', parentType);
  if (parentId) params.set('parent_id', parentId);
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/confluence/tree?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Confluence tree failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listNotionTreeNodes(
  parentType: 'root' | 'database' | 'page',
  parentId?: string,
  query?: string,
  limit: number = 30,
  pageToken?: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationTreeResponse> {
  const params = new URLSearchParams();
  params.set('parent_type', parentType);
  if (parentId) params.set('parent_id', parentId);
  if (query) params.set('query', query);
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  const url = `${API_BASE_URL}/api/v1/plugins/notion/tree?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion tree failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listConfluencePageAttachments(
  pageId: string,
  limit: number = 30,
  pageToken?: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationAttachmentsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/confluence/pages/${encodeURIComponent(pageId)}/attachments${qs}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Confluence attachments failed (${res.status}): ${text}`);
  }
  const data = await res.json() as CollaborationAttachmentsResponse;
  return {
    ...data,
    attachments: (data.attachments ?? []).map((item) => ({
      ...item,
      download_url: item.download_url && item.download_url.startsWith('/')
        ? `${API_BASE_URL}${item.download_url}`
        : item.download_url,
    })),
  };
}

export async function listNotionPageAttachments(
  pageId: string,
  limit: number = 30,
  pageToken?: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationAttachmentsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/notion/pages/${encodeURIComponent(pageId)}/attachments${qs}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion attachments failed (${res.status}): ${text}`);
  }
  const data = await res.json() as CollaborationAttachmentsResponse;
  return {
    ...data,
    attachments: (data.attachments ?? []).map((item) => ({
      ...item,
      download_url: item.download_url && item.download_url.startsWith('/')
        ? `${API_BASE_URL}${item.download_url}`
        : item.download_url,
    })),
  };
}

export async function getCollaborationAttachmentOpenTarget(
  downloadUrl: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationAttachmentOpenTarget> {
  const targetUrl = (downloadUrl || '').trim();
  if (!targetUrl) {
    throw new Error('첨부파일 URL이 비어 있습니다.');
  }

  const manualRes = await authFetch(targetUrl, {
    method: 'GET',
    headers: getHeaders(false),
    signal: options?.signal,
    redirect: 'manual',
  });

  if (manualRes.status >= 300 && manualRes.status < 400) {
    const locationHeader = manualRes.headers.get('Location');
    if (locationHeader) {
      return { url: _normalizeCollaborationOpenUrl(locationHeader) };
    }
  }

  if (manualRes.ok) {
    return _blobToOpenTarget(manualRes);
  }

  if (manualRes.type !== 'opaqueredirect') {
    const text = await manualRes.text();
    throw new Error(`Attachment open failed (${manualRes.status}): ${text}`);
  }

  const res = await authFetch(targetUrl, {
    method: 'GET',
    headers: getHeaders(false),
    signal: options?.signal,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Attachment open failed (${res.status}): ${text}`);
  }
  return _blobToOpenTarget(res);
}

export async function downloadCollaborationAttachment(
  downloadUrl: string,
  fallbackFileName: string,
  options?: CollaborationApiRequestOptions,
): Promise<File> {
  const targetUrl = (downloadUrl || '').trim();
  if (!targetUrl) {
    throw new Error('첨부파일 URL이 비어 있습니다.');
  }

  const res = await authFetch(targetUrl, {
    method: 'GET',
    headers: getHeaders(false),
    signal: options?.signal,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Attachment download failed (${res.status}): ${text}`);
  }

  const blob = await res.blob();
  const fileName = (fallbackFileName || 'attachment').trim() || 'attachment';
  return new File([blob], fileName, {
    type: blob.type || 'application/octet-stream',
  });
}

export async function listConfluencePages(
  query?: string,
  limit: number = 20,
  pageToken?: string,
  spaceKey?: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationListResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (spaceKey) params.set('space_key', spaceKey);
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/confluence/pages${qs}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Confluence list pages failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getConfluencePage(
  pageId: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationPageDetail> {
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/confluence/pages/${encodeURIComponent(pageId)}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Confluence get page failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getConfluencePagePdf(
  pageId: string,
  options?: CollaborationApiRequestOptions,
): Promise<Blob> {
  const url = `${API_BASE_URL}/api/v1/plugins/atlassian/confluence/pages/${encodeURIComponent(pageId)}/pdf`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Confluence get page pdf failed (${res.status}): ${text}`);
  }
  return res.blob();
}

export function getCollaborationPagePdfUrl(
  provider: CollaborationProvider,
  pageId: string,
): string {
  if (provider === 'confluence') {
    return `${API_BASE_URL}/api/v1/plugins/atlassian/confluence/pages/${encodeURIComponent(pageId)}/pdf`;
  }
  return `${API_BASE_URL}/api/v1/plugins/notion/pages/${encodeURIComponent(pageId)}/pdf`;
}

export async function listNotionPages(
  query?: string,
  limit: number = 20,
  pageToken?: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationListResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/notion/pages${qs}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion list pages failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getNotionPage(
  pageId: string,
  options?: CollaborationApiRequestOptions,
): Promise<CollaborationPageDetail> {
  const url = `${API_BASE_URL}/api/v1/plugins/notion/pages/${encodeURIComponent(pageId)}`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion get page failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getNotionPagePdf(
  pageId: string,
  options?: CollaborationApiRequestOptions,
): Promise<Blob> {
  const url = `${API_BASE_URL}/api/v1/plugins/notion/pages/${encodeURIComponent(pageId)}/pdf`;
  const res = await authFetch(url, { headers: getHeaders(), signal: options?.signal });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Notion get page pdf failed (${res.status}): ${text}`);
  }
  return res.blob();
}

/* ------------------------------------------------------------------ */
/* Gmail                                                               */
/* ------------------------------------------------------------------ */

/** Gmail OAuth 인증 URL 생성 */
export async function getGmailOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Gmail 연결 테스트 */
export async function testGmailConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Gmail 연결 해제 */
export async function disconnectGmail(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Google Drive                                                        */
/* ------------------------------------------------------------------ */

/** Google Drive OAuth 인증 URL 생성 */
export async function getGoogleDriveOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/google_drive/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Drive OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Google Drive 연결 테스트 */
export async function testGoogleDriveConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/google_drive/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Drive test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Google Drive 연결 해제 */
export async function disconnectGoogleDrive(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/google_drive/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Drive disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Cloud Drive File Browsing                                           */
/* ------------------------------------------------------------------ */

export interface DriveItem {
  id: string;
  name: string;
  mimeType?: string;
  isFolder?: boolean;
  type?: 'folder' | 'file';
  size?: number | null;
  modifiedTime?: string;
  lastModifiedDateTime?: string;
  shared?: boolean;
  iconLink?: string;
  thumbnailLink?: string;
  webUrl?: string;
  childCount?: number;
}

export interface DriveListResponse {
  items: DriveItem[];
  total: number;
  folderId?: string;
  folderPath?: string;
  query?: string;
  nextPageToken?: string | null;
}

export async function listGoogleDriveFiles(
  folderId: string = 'root',
  pageSize: number = 100,
  pageToken?: string,
): Promise<DriveListResponse> {
  const params = new URLSearchParams({ folder_id: folderId, page_size: String(pageSize) });
  if (pageToken) params.set('page_token', pageToken);
  const url = `${API_BASE_URL}/api/v1/plugins/google_drive/files?${params}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Drive list failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function searchGoogleDriveFiles(
  query: string,
  maxResults: number = 50,
  pageToken?: string,
): Promise<DriveListResponse> {
  const params = new URLSearchParams({ query, max_results: String(maxResults) });
  if (pageToken) params.set('page_token', pageToken);
  const url = `${API_BASE_URL}/api/v1/plugins/google_drive/search?${params}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Drive search failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listOneDriveFiles(
  folderPath: string = '',
  folderId: string = '',
  pageSize: number = 100,
  skipToken?: string,
): Promise<DriveListResponse> {
  const params = new URLSearchParams({ folder_path: folderPath, folder_id: folderId, page_size: String(pageSize) });
  if (skipToken) params.set('skip_token', skipToken);
  const url = `${API_BASE_URL}/api/v1/plugins/onedrive/files?${params}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`OneDrive list failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function searchOneDriveFiles(
  query: string,
  maxResults: number = 50,
  skipToken?: string,
): Promise<DriveListResponse> {
  const params = new URLSearchParams({ query, max_results: String(maxResults) });
  if (skipToken) params.set('skip_token', skipToken);
  const url = `${API_BASE_URL}/api/v1/plugins/onedrive/search?${params}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`OneDrive search failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ---- Google Drive Picker Config ---- */

export interface GooglePickerConfig {
  api_key: string;
  oauth_token: string;
  app_id: string;
}

export async function getGoogleDrivePickerConfig(): Promise<GooglePickerConfig> {
  const url = `${API_BASE_URL}/api/v1/plugins/google_drive/picker-config`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Drive picker config failed (${res.status}): ${text}`);
  }
  return res.json();
}

export function getCloudDriveDownloadUrl(cloudSource: 'google_drive' | 'onedrive', fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/${cloudSource}/download/${encodeURIComponent(fileId)}`;
}

export async function downloadCloudFile(
  cloudSource: 'google_drive' | 'onedrive',
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getCloudDriveDownloadUrl(cloudSource, fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Cloud file download failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

/* ------------------------------------------------------------------ */
/* Gmail Mail API                                                      */
/* ------------------------------------------------------------------ */

export type MailProvider = 'gmail' | 'outlook';

export interface EmailAttachmentMeta {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  message_id: string;
}

export interface EmailItem {
  id: string;
  thread_id?: string;
  conversation_id?: string;
  subject: string;
  sender: string;
  recipients: string[];
  cc: string[];
  snippet: string;
  date: string;
  is_read: boolean;
  has_attachments: boolean;
  labels: string[];
  attachments: EmailAttachmentMeta[];
  body: string;
}

export interface EmailListResponse {
  messages: EmailItem[];
  nextPageToken: string | null;
}

export interface GmailLabel {
  id: string;
  name: string;
  type: string;
  messages_total: number | null;
  messages_unread: number | null;
}

export interface OutlookFolder {
  id: string;
  name: string;
  total_count: number;
  unread_count: number;
}

export type CalendarProviderId = 'widget_calendar' | 'google_calendar' | 'outlook_calendar' | 'zoom';
export type MailProviderId = 'gmail' | 'outlook';
export type ConferenceProvider = 'none' | 'google_meet' | 'teams' | 'zoom';

export interface CalendarProviderInfo {
  id: CalendarProviderId;
  label: string;
  connected: boolean;
  can_view: boolean;
  can_create: boolean;
  reauth_required: boolean;
  create_error?: string | null;
  account_label?: string | null;
  description?: string | null;
  color?: string | null;
}

export interface CalendarEventSource {
  provider: CalendarProviderId;
  external_id: string;
  external_url?: string | null;
  meeting_url?: string | null;
  synced_by_widget: boolean;
}

export interface CalendarGuest {
  email: string;
  name?: string | null;
}

export interface CalendarGuestSuggestion {
  email: string;
  name?: string | null;
  provider: 'gmail' | 'outlook' | 'google_calendar';
  source: string;
}

export interface CalendarGuestSuggestionProviderError {
  plugin_id: string;
  message?: string | null;
  reauth_required: boolean;
}

export interface CalendarInviteSender {
  provider: MailProviderId;
  email: string;
  label: string;
}

export interface CalendarEventItem {
  id: string;
  group_id?: string | null;
  provider: CalendarProviderId;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  location?: string | null;
  description?: string | null;
  meeting_url?: string | null;
  external_url?: string | null;
  readonly: boolean;
  local_only: boolean;
  guests: CalendarGuest[];
  badges: string[];
  sources: CalendarEventSource[];
}

export interface CalendarProvidersResponse {
  providers: CalendarProviderInfo[];
}

export interface CalendarEventsResponse {
  providers: CalendarProviderInfo[];
  events: CalendarEventItem[];
  start: string;
  end: string;
}

export interface CalendarGuestSuggestionsResponse {
  suggestions: CalendarGuestSuggestion[];
  senders: CalendarInviteSender[];
  provider_errors: CalendarGuestSuggestionProviderError[];
}

export interface CreateCalendarEventsRequest {
  title: string;
  start: string;
  end: string;
  all_day?: boolean;
  location?: string;
  description?: string;
  timezone?: string;
  guests?: CalendarGuest[];
  send_invite_email?: boolean;
  invite_sender_provider?: MailProviderId | null;
  target_providers: CalendarProviderId[];
  conference_provider?: ConferenceProvider;
}

export interface CalendarProviderCreateResult {
  provider: CalendarProviderId;
  success: boolean;
  external_id?: string | null;
  external_url?: string | null;
  meeting_url?: string | null;
  error?: string | null;
}

export interface CreateCalendarEventsResponse {
  success: boolean;
  group_id?: string | null;
  event?: CalendarEventItem | null;
  results: CalendarProviderCreateResult[];
  invite_email_sent: boolean;
  invite_email_error?: string | null;
  conference_url?: string | null;
}

export interface UpdateCalendarEventRequest {
  title: string;
  start: string;
  end: string;
  all_day?: boolean;
  location?: string;
  description?: string;
  timezone?: string;
  guests?: CalendarGuest[];
  send_invite_email?: boolean;
  invite_sender_provider?: MailProviderId | null;
  conference_provider?: ConferenceProvider;
}

export interface CalendarProviderMutationResult {
  provider: CalendarProviderId;
  success: boolean;
  error?: string | null;
}

export interface UpdateCalendarEventResponse {
  success: boolean;
  group_id?: string | null;
  event?: CalendarEventItem | null;
  results: CalendarProviderMutationResult[];
  invite_email_sent: boolean;
  invite_email_error?: string | null;
  conference_url?: string | null;
}

export interface DeleteCalendarEventResponse {
  success: boolean;
  group_id?: string | null;
  results: CalendarProviderMutationResult[];
}

export type MailApiRequestOptions = {
  signal?: AbortSignal;
  maxRetries?: number;
};

type RateLimitRetryError = Error & {
  code: 'RATE_LIMIT_RETRY_EXHAUSTED';
  status: 429;
  retryAfterMs: number;
};

const DEFAULT_MAIL_RETRY_COUNT = 2;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function parseRetryAfterMs(res: Response, attempt: number): number {
  const retryAfter = res.headers.get('Retry-After');
  if (retryAfter) {
    const asNum = Number(retryAfter);
    if (Number.isFinite(asNum)) return Math.max(0, asNum * 1000);
    const asDate = Date.parse(retryAfter);
    if (!Number.isNaN(asDate)) return Math.max(0, asDate - Date.now());
  }
  const base = 400 * Math.pow(2, attempt);
  const jitter = Math.floor(Math.random() * 250);
  return base + jitter;
}

async function authFetchWithRateLimitRetry(
  url: string,
  init: RequestInit,
  options?: MailApiRequestOptions,
): Promise<Response> {
  const maxRetries = options?.maxRetries ?? DEFAULT_MAIL_RETRY_COUNT;
  let attempt = 0;
  let lastRetryAfterMs = 0;

  while (true) {
    const res = await authFetch(url, init);
    if (res.status !== 429) return res;

    lastRetryAfterMs = parseRetryAfterMs(res, attempt);
    if (attempt >= maxRetries) {
      const err = new Error(
        `Rate limit retry exhausted after ${attempt} retries`,
      ) as RateLimitRetryError;
      err.code = 'RATE_LIMIT_RETRY_EXHAUSTED';
      err.status = 429;
      err.retryAfterMs = lastRetryAfterMs;
      throw err;
    }

    attempt += 1;
    await sleep(lastRetryAfterMs);
  }
}

export async function listGmailMessages(
  query?: string,
  label?: string,
  maxResults?: number,
  pageToken?: string,
  options?: MailApiRequestOptions,
): Promise<EmailListResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (label) params.set('label', label);
  if (maxResults) params.set('max_results', String(maxResults));
  if (pageToken) params.set('page_token', pageToken);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/messages${qs}`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail list messages failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getGmailMessage(
  messageId: string,
  options?: MailApiRequestOptions,
): Promise<EmailItem> {
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/messages/${encodeURIComponent(messageId)}`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail get message failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getGmailThread(
  threadId: string,
  options?: MailApiRequestOptions,
): Promise<{ thread_id: string; messages: EmailItem[] }> {
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/threads/${encodeURIComponent(threadId)}`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail get thread failed (${res.status}): ${text}`);
  }
  return res.json();
}

export function getGmailAttachmentDownloadUrl(
  messageId: string,
  attachmentId: string,
  fileName?: string,
): string {
  const base = `${API_BASE_URL}/api/v1/plugins/gmail/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
  if (!fileName) return base;
  return `${base}?filename=${encodeURIComponent(fileName)}`;
}

export async function downloadGmailAttachment(
  messageId: string,
  attachmentId: string,
  fileName: string,
  options?: MailApiRequestOptions,
): Promise<File> {
  const url = getGmailAttachmentDownloadUrl(messageId, attachmentId, fileName);
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail attachment download failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

export async function listGmailLabels(
  options?: MailApiRequestOptions,
): Promise<{ labels: GmailLabel[] }> {
  const url = `${API_BASE_URL}/api/v1/plugins/gmail/labels`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Gmail list labels failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Outlook Mail API                                                    */
/* ------------------------------------------------------------------ */

export async function listOutlookMessages(
  query?: string,
  folder?: string,
  limit?: number,
  skipToken?: string,
  options?: MailApiRequestOptions,
): Promise<EmailListResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (folder) params.set('folder', folder);
  if (limit) params.set('limit', String(limit));
  if (skipToken) params.set('skip_token', skipToken);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/messages${qs}`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook list messages failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getOutlookMessage(
  messageId: string,
  options?: MailApiRequestOptions,
): Promise<EmailItem> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/messages/${encodeURIComponent(messageId)}`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook get message failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function getOutlookConversation(
  messageId: string,
  options?: MailApiRequestOptions,
): Promise<{ conversation_id: string; messages: EmailItem[] }> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/messages/${encodeURIComponent(messageId)}/conversation`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook get conversation failed (${res.status}): ${text}`);
  }
  return res.json();
}

export function getOutlookAttachmentDownloadUrl(
  messageId: string,
  attachmentId: string,
  fileName?: string,
): string {
  const base = `${API_BASE_URL}/api/v1/plugins/outlook/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
  if (!fileName) return base;
  return `${base}?filename=${encodeURIComponent(fileName)}`;
}

export async function downloadOutlookAttachment(
  messageId: string,
  attachmentId: string,
  fileName: string,
  options?: MailApiRequestOptions,
): Promise<File> {
  const url = getOutlookAttachmentDownloadUrl(messageId, attachmentId, fileName);
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook attachment download failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

export async function listOutlookFolders(
  options?: MailApiRequestOptions,
): Promise<{ folders: OutlookFolder[] }> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/folders`;
  const res = await authFetchWithRateLimitRetry(
    url,
    { headers: getHeaders(), signal: options?.signal },
    options,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook list folders failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function downloadMailAttachment(
  mailSource: MailProvider,
  messageId: string,
  attachmentId: string,
  fileName: string,
  options?: MailApiRequestOptions,
): Promise<File> {
  if (mailSource === 'gmail') {
    return downloadGmailAttachment(messageId, attachmentId, fileName, options);
  }
  return downloadOutlookAttachment(messageId, attachmentId, fileName, options);
}

export function getMailAttachmentDownloadUrl(
  mailSource: MailProvider,
  messageId: string,
  attachmentId: string,
  fileName?: string,
): string {
  if (mailSource === 'gmail') {
    return getGmailAttachmentDownloadUrl(messageId, attachmentId, fileName);
  }
  return getOutlookAttachmentDownloadUrl(messageId, attachmentId, fileName);
}

/* ------------------------------------------------------------------ */
/* Google Calendar                                                     */
/* ------------------------------------------------------------------ */

/** Google Calendar OAuth 인증 URL 생성 */
export async function getGoogleCalendarOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/google_calendar/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Calendar OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Google Calendar 연결 테스트 */
export async function testGoogleCalendarConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/google_calendar/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Calendar test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Google Calendar 연결 해제 */
export async function disconnectGoogleCalendar(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/google_calendar/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google Calendar disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Discord                                                              */
/* ------------------------------------------------------------------ */

/** Discord 플러그인 상태 조회 */
export async function getDiscordStatus(): Promise<PluginInfo> {
  const url = `${API_BASE_URL}/api/v1/plugins/discord/status`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Discord 연결 검증 (MCP 서버 실제 연결 테스트) */
export async function verifyDiscord(): Promise<PluginVerifyResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/discord/verify`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord verify failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Discord OAuth 인증 URL 생성 */
export async function getDiscordOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/discord/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface DiscordInviteUrlResponse {
  invite_url: string;
  client_id: string;
  scopes: string[];
  permissions: string;
  guild_id?: string | null;
  requires_admin: boolean;
}

/** Discord bot invite URL 생성 */
export async function getDiscordInviteUrl(
  guildId?: string,
): Promise<DiscordInviteUrlResponse> {
  const params = new URLSearchParams();
  if (guildId) params.set('guild_id', guildId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/discord/invite-url${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord invite URL failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Discord OAuth 콜백 처리 (프론트에서 code/state 직접 전달) */
export async function discordOAuthCallback(
  code: string,
  state: string,
): Promise<OAuthCallbackResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/discord/oauth/callback`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord OAuth callback failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Discord OAuth 연결 상태 조회 */
export async function getDiscordOAuthStatus(
  _userId?: string,
): Promise<SlackConnectionStatus> {
  const url = `${API_BASE_URL}/api/v1/plugins/discord/status`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord OAuth status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Discord 연결 테스트 (저장된 토큰으로 /users/@me) */
export async function testDiscordConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/discord/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Discord 연결 해제 */
export async function disconnectDiscord(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/discord/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface DiscordGuildItem {
  id: string;
  name: string;
  icon?: string | null;
  owner?: boolean;
}

export interface DiscordGuildsResponse {
  guilds: DiscordGuildItem[];
  nextCursor: string | null;
  total: number;
}

export interface DiscordChannelItem {
  id: string;
  name: string;
  type: number;
  position?: number;
  guildId: string;
}

export interface DiscordChannelsResponse {
  channels: DiscordChannelItem[];
  total: number;
}

export interface DiscordChannelFileItem {
  id: string;
  name: string;
  mimeType?: string;
  size?: number;
  channelId: string;
  messageId: string;
  attachmentId: string;
}

export interface DiscordChannelFilesResponse {
  files: DiscordChannelFileItem[];
  nextCursor: string | null;
  total: number;
}

export async function listDiscordGuilds(
  limit: number = 50,
  cursor?: string,
  query?: string,
): Promise<DiscordGuildsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (query) params.set('query', query);
  const url = `${API_BASE_URL}/api/v1/plugins/discord/guilds?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord guilds failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listDiscordChannels(
  guildId: string,
  query?: string,
): Promise<DiscordChannelsResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/discord/guilds/${encodeURIComponent(guildId)}/channels${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord channels failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listDiscordChannelFiles(
  channelId: string,
  limit: number = 50,
  cursor?: string,
  query?: string,
): Promise<DiscordChannelFilesResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  if (query) params.set('query', query);
  const url = `${API_BASE_URL}/api/v1/plugins/discord/channels/${encodeURIComponent(channelId)}/files?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord channel files failed (${res.status}): ${text}`);
  }
  return res.json();
}

export function getDiscordFileDownloadUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/discord/files/${encodeURIComponent(fileId)}/download`;
}

export function getDiscordFileThumbnailUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/discord/files/${encodeURIComponent(fileId)}/thumbnail`;
}

export async function downloadDiscordFile(
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getDiscordFileDownloadUrl(fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord file download failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

export async function downloadDiscordFileThumbnail(
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getDiscordFileThumbnailUrl(fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Discord file thumbnail failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'image/*' });
}

/* ------------------------------------------------------------------ */
/* GitHub                                                              */
/* ------------------------------------------------------------------ */

/** GitHub OAuth 인증 URL 생성 */
export async function getGithubOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/github/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GitHub OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** GitHub 연결 해제 */
export async function disconnectGithub(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/github/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GitHub disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Microsoft Teams                                                     */
/* ------------------------------------------------------------------ */

/** Microsoft Teams OAuth 인증 URL 생성 */
export async function getMicrosoftTeamsOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Microsoft Teams OAuth 콜백 처리 */
export async function microsoftTeamsOAuthCallback(
  code: string,
  state: string,
): Promise<OAuthCallbackResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/oauth/callback`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams OAuth callback failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Microsoft Teams 연결 테스트 */
export async function testMicrosoftTeamsConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Microsoft Teams 연결 해제 */
export async function disconnectMicrosoftTeams(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface MicrosoftTeamsTeamItem {
  id: string;
  name: string;
  description?: string;
}

export interface MicrosoftTeamsTeamsResponse {
  teams: MicrosoftTeamsTeamItem[];
  nextPageToken: string | null;
  total: number;
}

export interface MicrosoftTeamsChannelItem {
  id: string;
  name: string;
  description?: string;
  membershipType?: string;
  teamId: string;
}

export interface MicrosoftTeamsChannelsResponse {
  channels: MicrosoftTeamsChannelItem[];
  total: number;
}

export interface MicrosoftTeamsChannelFileItem {
  id: string;
  name: string;
  mimeType?: string;
  size?: number;
  lastModifiedDateTime?: string;
  teamId: string;
  channelId: string;
}

export interface MicrosoftTeamsChannelFilesResponse {
  files: MicrosoftTeamsChannelFileItem[];
  nextPageToken: string | null;
  total: number;
}

export async function listMicrosoftTeamsTeams(
  limit: number = 50,
  pageToken?: string,
  query?: string,
): Promise<MicrosoftTeamsTeamsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  if (query) params.set('query', query);
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/teams?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams teams failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listMicrosoftTeamsChannels(
  teamId: string,
  query?: string,
): Promise<MicrosoftTeamsChannelsResponse> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/teams/${encodeURIComponent(teamId)}/channels${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams channels failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listMicrosoftTeamsChannelFiles(
  teamId: string,
  channelId: string,
  limit: number = 50,
  pageToken?: string,
  query?: string,
): Promise<MicrosoftTeamsChannelFilesResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (pageToken) params.set('page_token', pageToken);
  if (query) params.set('query', query);
  const url = `${API_BASE_URL}/api/v1/plugins/microsoft_teams/teams/${encodeURIComponent(teamId)}/channels/${encodeURIComponent(channelId)}/files?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams channel files failed (${res.status}): ${text}`);
  }
  return res.json();
}

export function getMicrosoftTeamsFileDownloadUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/microsoft_teams/files/${encodeURIComponent(fileId)}/download`;
}

export function getMicrosoftTeamsFileThumbnailUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/plugins/microsoft_teams/files/${encodeURIComponent(fileId)}/thumbnail`;
}

export async function downloadMicrosoftTeamsFile(
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getMicrosoftTeamsFileDownloadUrl(fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams file download failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

export async function downloadMicrosoftTeamsFileThumbnail(
  fileId: string,
  fileName: string,
): Promise<File> {
  const url = getMicrosoftTeamsFileThumbnailUrl(fileId);
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Microsoft Teams file thumbnail failed (${res.status}): ${text}`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'image/*' });
}

export type MessengerProvider = 'slack' | 'microsoft_teams' | 'discord';

export interface MessengerChannelItem {
  id: string;
  provider: MessengerProvider;
  name: string;
  teamId?: string;
  guildId?: string;
  subtitle?: string;
}

export interface MessengerFileItem {
  id: string;
  provider: MessengerProvider;
  channelId: string;
  teamId?: string;
  guildId?: string;
  name: string;
  mimeType?: string;
  size?: number;
}

export function getMessengerAttachmentDownloadUrl(
  source: MessengerProvider,
  fileId: string,
): string {
  if (source === 'slack') return getSlackFileDownloadUrl(fileId);
  if (source === 'microsoft_teams') return getMicrosoftTeamsFileDownloadUrl(fileId);
  return getDiscordFileDownloadUrl(fileId);
}

export async function downloadMessengerAttachment(
  source: MessengerProvider,
  fileId: string,
  fileName: string,
): Promise<File> {
  if (source === 'slack') return downloadSlackFile(fileId, fileName);
  if (source === 'microsoft_teams') return downloadMicrosoftTeamsFile(fileId, fileName);
  return downloadDiscordFile(fileId, fileName);
}

export function getMessengerAttachmentThumbnailUrl(
  source: MessengerProvider,
  fileId: string,
): string {
  if (source === 'slack') return getSlackFileThumbnailUrl(fileId);
  if (source === 'microsoft_teams') return getMicrosoftTeamsFileThumbnailUrl(fileId);
  return getDiscordFileThumbnailUrl(fileId);
}

export async function downloadMessengerAttachmentThumbnail(
  source: MessengerProvider,
  fileId: string,
  fileName: string,
): Promise<File> {
  if (source === 'slack') return downloadSlackFileThumbnail(fileId, fileName);
  if (source === 'microsoft_teams') return downloadMicrosoftTeamsFileThumbnail(fileId, fileName);
  return downloadDiscordFileThumbnail(fileId, fileName);
}

/* ------------------------------------------------------------------ */
/* Zoom                                                                */
/* ------------------------------------------------------------------ */

/** Zoom OAuth 인증 URL 생성 */
export async function getZoomOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/zoom/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Zoom OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Zoom 연결 해제 */
export async function disconnectZoom(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/zoom/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Zoom disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Outlook Mail                                                        */
/* ------------------------------------------------------------------ */

/** Outlook Mail OAuth 인증 URL 생성 */
export async function getOutlookOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Mail OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Outlook Mail OAuth 콜백 처리 */
export async function outlookOAuthCallback(
  code: string,
  state: string,
): Promise<OAuthCallbackResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/oauth/callback`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Mail OAuth callback failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Outlook Mail 연결 테스트 */
export async function testOutlookConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Mail test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Outlook Mail 연결 해제 */
export async function disconnectOutlook(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Mail disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Outlook Calendar                                                    */
/* ------------------------------------------------------------------ */

/** Outlook Calendar OAuth 인증 URL 생성 */
export async function getOutlookCalendarOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/outlook_calendar/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Calendar OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Outlook Calendar OAuth 콜백 처리 */
export async function outlookCalendarOAuthCallback(
  code: string,
  state: string,
): Promise<OAuthCallbackResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook_calendar/oauth/callback`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Calendar OAuth callback failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Outlook Calendar 연결 테스트 */
export async function testOutlookCalendarConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook_calendar/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Calendar test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Outlook Calendar 연결 해제 */
export async function disconnectOutlookCalendar(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/outlook_calendar/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Outlook Calendar disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Calendar Widget API                                                 */
/* ------------------------------------------------------------------ */

export async function listCalendarProviders(): Promise<CalendarProvidersResponse> {
  const url = `${API_BASE_URL}/api/v1/calendar/providers`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar providers failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function listCalendarEvents(params: {
  start: string;
  end: string;
  providers?: CalendarProviderId[];
}): Promise<CalendarEventsResponse> {
  const query = new URLSearchParams();
  query.set('start', params.start);
  query.set('end', params.end);
  if (params.providers !== undefined) {
    query.set('providers', params.providers.join(','));
  }
  const url = `${API_BASE_URL}/api/v1/calendar/events?${query.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar events failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function createCalendarEvents(
  payload: CreateCalendarEventsRequest,
): Promise<CreateCalendarEventsResponse> {
  const url = `${API_BASE_URL}/api/v1/calendar/events`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar create failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function searchCalendarGuests(
  query?: string,
  limit = 8,
): Promise<CalendarGuestSuggestionsResponse> {
  const params = new URLSearchParams();
  if (query !== undefined) params.set('query', query);
  params.set('limit', String(limit));
  const url = `${API_BASE_URL}/api/v1/calendar/guests/search?${params.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar guest search failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function updateCalendarEvent(
  groupId: string,
  payload: UpdateCalendarEventRequest,
): Promise<UpdateCalendarEventResponse> {
  const url = `${API_BASE_URL}/api/v1/calendar/events/${encodeURIComponent(groupId)}`;
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar update failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function updateCalendarProviderEvent(
  provider: Exclude<CalendarProviderId, 'widget_calendar'>,
  externalId: string,
  payload: UpdateCalendarEventRequest,
): Promise<UpdateCalendarEventResponse> {
  const url = `${API_BASE_URL}/api/v1/calendar/provider-events/${encodeURIComponent(provider)}/${encodeURIComponent(externalId)}`;
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar provider update failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function deleteCalendarEvent(
  groupId: string,
): Promise<DeleteCalendarEventResponse> {
  const url = `${API_BASE_URL}/api/v1/calendar/events/${encodeURIComponent(groupId)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar delete failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function deleteCalendarProviderEvent(
  provider: Exclude<CalendarProviderId, 'widget_calendar'>,
  externalId: string,
): Promise<DeleteCalendarEventResponse> {
  const url = `${API_BASE_URL}/api/v1/calendar/provider-events/${encodeURIComponent(provider)}/${encodeURIComponent(externalId)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Calendar provider delete failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* OneDrive                                                            */
/* ------------------------------------------------------------------ */

/** OneDrive OAuth 인증 URL 생성 */
export async function getOneDriveOAuthUrl(
  redirectUri?: string,
): Promise<OAuthAuthorizeResponse> {
  const params = new URLSearchParams();
  if (redirectUri) params.set('redirect_uri', redirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/plugins/onedrive/oauth/authorize${qs}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`OneDrive OAuth authorize failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** OneDrive OAuth 콜백 처리 */
export async function onedriveOAuthCallback(
  code: string,
  state: string,
): Promise<OAuthCallbackResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/onedrive/oauth/callback`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`OneDrive OAuth callback failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** OneDrive 연결 테스트 */
export async function testOneDriveConnection(
  _userId?: string,
): Promise<SlackTestResult> {
  const url = `${API_BASE_URL}/api/v1/plugins/onedrive/test`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`OneDrive test failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** OneDrive 연결 해제 */
export async function disconnectOneDrive(
  _userId?: string,
): Promise<PluginDisconnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/onedrive/disconnect`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`OneDrive disconnect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Provider 상태 조회 (공통) */
export async function getProviderStatus(
  providerId: string,
  _userId?: string,
): Promise<PluginInfo> {
  const url = `${API_BASE_URL}/api/v1/plugins/${encodeURIComponent(providerId)}/status`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${providerId} status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Provider 연결 검증 (공통) */
export async function verifyProvider(
  providerId: string,
  _userId?: string,
): Promise<PluginVerifyResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/${encodeURIComponent(providerId)}/verify`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${providerId} verify failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Provider API Key 연결 (공통) */
export async function connectProviderApiKey(
  providerId: string,
  credentials: Record<string, string>,
  _userId?: string,
): Promise<ApiKeyConnectResponse> {
  const url = `${API_BASE_URL}/api/v1/plugins/${encodeURIComponent(providerId)}/connect/apikey`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ credentials }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${providerId} apikey connect failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 플러그인 사용자 설정 업데이트 (send_as 등) */
export async function updateProviderSettings(
  providerId: string,
  settings: { send_as?: string },
): Promise<{ success: boolean; provider: string; message: string }> {
  const url = `${API_BASE_URL}/api/v1/plugins/${encodeURIComponent(providerId)}/settings`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(settings),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${providerId} settings update failed (${res.status}): ${text}`);
  }
  return res.json();
}

export class AuthMeError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuthMeError';
    this.status = status;
  }
}

export async function getAuthMe(): Promise<AuthMeResponse> {
  const url = `${API_BASE_URL}/api/v1/auth/me`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new AuthMeError(res.status, `Auth me failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* User Plan & Usage                                                   */
/* ------------------------------------------------------------------ */

export interface UserUsageResponse {
  tokens_used: number;
  tokens_limit: number;
  storage_used_bytes: number;
  storage_limit_bytes: number;
}

export interface UpdatePlanResponse {
  success: boolean;
  plan: string;
  message: string;
}

export interface CreateBillingCheckoutRequest {
  plan: 'pro' | 'premium' | 'max' | 'business';
  cycle: 'monthly' | 'yearly';
}

export interface CreateBillingCheckoutResponse {
  checkout_url: string;
  plan: string;
  cycle: string;
  trial_days: number;
  used_trial_price: boolean;
  transaction_id?: string | null;
}

export interface BillingPaymentMethodSummary {
  payment_method_id?: string | null;
  type?: string | null;
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
  origin?: string | null;
  saved_at?: string | null;
}

export interface BillingStatus {
  enabled: boolean;
  provider: string;
  current_plan: string;
  billing_status?: string | null;
  customer_id?: string | null;
  subscription_id?: string | null;
  subscription_plan?: string | null;
  billing_cycle?: string | null;
  cancel_at_period_end?: boolean;
  current_period_start?: string | null;
  current_period_end?: string | null;
  trial_ends_at?: string | null;
  next_billed_at?: string | null;
  scheduled_plan?: string | null;
  scheduled_cycle?: string | null;
  scheduled_change_action?: string | null;
  scheduled_change_effective_at?: string | null;
  payment_method?: BillingPaymentMethodSummary | null;
  payment_method_supported?: boolean;
  management_urls?: Record<string, string>;
  grace_until?: string | null;
  over_limit_tokens?: boolean;
  over_limit_storage?: boolean;
  over_limit_team_spaces?: boolean;
  can_start_pro_trial: boolean;
  pro_trial_used_at?: string | null;
}

export interface BillingCatalogPrice {
  price_id: string;
  plan: string;
  cycle: 'monthly' | 'yearly';
  amount: number;
  amount_minor: number;
  currency_code: string;
  monthly_equivalent?: number | null;
  billing_interval?: string | null;
  billing_frequency?: number | null;
  trial_days: number;
}

export interface BillingCatalogPlan {
  monthly?: BillingCatalogPrice | null;
  yearly?: BillingCatalogPrice | null;
}

export interface BillingCatalog {
  provider: string;
  cached_at?: string | null;
  plans: Record<string, BillingCatalogPlan>;
}

export interface CreateBillingPortalResponse {
  portal_url: string;
}

export interface CancelBillingSubscriptionResponse {
  success: boolean;
  subscription_id: string;
  billing_status: string;
  cancel_at_period_end: boolean;
  current_period_end?: string | null;
}

export interface ResumeBillingSubscriptionResponse {
  success: boolean;
  subscription_id: string;
  billing_status: string;
  cancel_at_period_end: boolean;
}

export interface ChangeBillingSubscriptionPlanRequest {
  plan: 'pro' | 'premium' | 'max' | 'business';
  cycle: 'monthly' | 'yearly';
}

export interface ChangeBillingSubscriptionPlanResponse {
  success: boolean;
  subscription_id: string;
  current_plan: string;
  target_plan: string;
  target_cycle: string;
  proration_billing_mode: string;
  effective_timing: 'immediate' | 'next_billing_period';
  pending_webhook_sync: boolean;
}

export interface TrialUpgradeCheckoutRequest {
  plan: 'pro' | 'premium' | 'max' | 'business';
  cycle: 'monthly' | 'yearly';
}

export interface TrialUpgradeCheckoutResponse {
  checkout_url: string;
  plan: string;
  cycle: string;
  previous_subscription_id: string;
  transaction_id?: string | null;
}

export interface BillingOpsMetricsResponse {
  window_hours: number;
  webhook_status_counts: Record<string, number>;
  webhook_total: number;
  recent_error_count: number;
  latest_processed_at?: string | null;
}

export interface BillingTimelineEntry {
  at?: string | null;
  source: string;
  action: string;
  user_id?: string | null;
  subscription_id?: string | null;
  details: Record<string, unknown>;
}

export interface BillingTimelineResponse {
  user_id: string;
  entries: BillingTimelineEntry[];
}

/** 사용자 사용량 조회 */
export async function getUserUsage(): Promise<UserUsageResponse> {
  const url = `${API_BASE_URL}/api/v1/users/usage`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`User usage failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Paddle 체크아웃 링크 생성 */
export async function createBillingCheckout(
  body: CreateBillingCheckoutRequest,
): Promise<CreateBillingCheckoutResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/checkout`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Checkout create failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 내 결제 상태 조회 */
export async function getBillingStatus(): Promise<BillingStatus> {
  const url = `${API_BASE_URL}/api/v1/billing/me`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Billing status failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Paddle 가격 카탈로그 조회 */
export async function getBillingCatalog(): Promise<BillingCatalog> {
  const url = `${API_BASE_URL}/api/v1/billing/catalog`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Billing catalog failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Paddle 고객 포털 URL 생성 */
export async function createBillingPortalSession(
  subscriptionId?: string,
  purpose: 'manage' | 'cancel' | 'billing' = 'manage',
): Promise<CreateBillingPortalResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/portal`;
  const body = subscriptionId
    ? { subscription_id: subscriptionId, purpose }
    : { purpose };
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Billing portal failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 구독 기간말 해지 예약 */
export async function cancelBillingSubscription(
  subscriptionId?: string,
): Promise<CancelBillingSubscriptionResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/subscription/cancel`;
  const body = subscriptionId ? { subscription_id: subscriptionId } : {};
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Cancel subscription failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 구독 해지 예약 취소 */
export async function resumeBillingSubscription(
  subscriptionId?: string,
): Promise<ResumeBillingSubscriptionResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/subscription/resume`;
  const body = subscriptionId ? { subscription_id: subscriptionId } : {};
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Resume subscription failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 유료 구독 플랜/주기 변경 */
export async function changeBillingSubscriptionPlan(
  body: ChangeBillingSubscriptionPlanRequest,
): Promise<ChangeBillingSubscriptionPlanResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/subscription/change-plan`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Change subscription plan failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Trial 구독 consent lock 시 즉시 해지 + 신규 checkout 생성 */
export async function trialUpgradeCheckout(
  body: TrialUpgradeCheckoutRequest,
): Promise<TrialUpgradeCheckoutResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/subscription/trial-upgrade`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Trial upgrade checkout failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** Billing 운영 메트릭 (admin) */
export async function getBillingOpsMetrics(): Promise<BillingOpsMetricsResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/ops/metrics`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Billing ops metrics failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 사용자 결제 타임라인 (admin) */
export async function getBillingUserTimeline(userId: string): Promise<BillingTimelineResponse> {
  const url = `${API_BASE_URL}/api/v1/billing/ops/timeline/${encodeURIComponent(userId)}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Billing user timeline failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 사용자 플랜 변경 (mock — 결제 연동 전) */
export async function updateUserPlan(plan: string): Promise<UpdatePlanResponse> {
  const url = `${API_BASE_URL}/api/v1/user/plan`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Update plan failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* User Profile                                                        */
/* ------------------------------------------------------------------ */

export interface ProfileUpdateRequest {
  name?: string | null;
  picture?: string | null;
  company?: string | null;
  department?: string | null;
  preferred_language?: string | null;
  country_code?: string | null;
  timezone?: string | null;
  agent_pictures?: Record<string, string> | null;
  sidebar_favorite_modes?: string[] | null;
  tools_favorite_ids?: string[] | null;
  job_role?: string | null;
  interests?: string[] | null;
  custom_interests?: string[] | null;
  feed_delivery_hour?: number | null;
  feed_delivery_hour_locked_until?: string | null;
  onboarding_completed?: boolean | null;
}

export interface ProfileUpdateResponse {
  success: boolean;
  user_id: string;
  name: string | null;
  picture: string | null;
  has_custom_picture: boolean;
  company: string | null;
  department: string | null;
  preferred_language: string | null;
  country_code: string | null;
  timezone?: string | null;
  agent_pictures?: Record<string, string> | null;
  sidebar_favorite_modes?: string[] | null;
  tools_favorite_ids?: string[] | null;
  job_role?: string | null;
  interests?: string[] | null;
  custom_interests?: string[] | null;
  feed_delivery_hour?: number | null;
  feed_delivery_hour_locked_until?: string | null;
  onboarding_completed?: boolean;
}

export async function updateMyProfile(
  body: ProfileUpdateRequest,
): Promise<ProfileUpdateResponse> {
  const url = `${API_BASE_URL}/api/v1/users/me/profile`;
  const res = await authFetch(url, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Profile update failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface DeleteAccountResponse {
  success: boolean;
  message: string;
}

export async function deleteMyAccount(): Promise<DeleteAccountResponse> {
  const url = `${API_BASE_URL}/api/v1/users/me`;
  const res = await authFetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      if (data && typeof data === 'object') {
        detail = String((data as { detail?: unknown; message?: unknown }).detail ?? (data as { message?: unknown }).message ?? '');
      }
    } catch {
      // JSON이 아니면 fallback
    }
    if (!detail) {
      try {
        detail = await res.text();
      } catch {
        detail = '';
      }
    }
    throw new Error(detail || `Account delete failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Consent (동의) 관리                                                  */
/* ------------------------------------------------------------------ */

export interface ConsentState {
  agreed: boolean;
  version: string;
  agreed_at: string | null;
}

export interface CookieConsentState {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  version: string;
  agreed_at: string | null;
}

export type ConsentsMap = {
  terms_of_service?: ConsentState;
  privacy_policy?: ConsentState;
  third_party_sharing?: ConsentState;
  marketing?: ConsentState;
  cookie?: CookieConsentState;
};

export interface ConsentUpdateItem {
  consent_type: 'terms_of_service' | 'privacy_policy' | 'third_party_sharing' | 'marketing' | 'cookie';
  agreed?: boolean;
  version: string;
  categories?: Record<string, boolean>;
}

export interface ConsentsResponse {
  success: boolean;
  consents: ConsentsMap;
}

export async function updateConsents(
  items: ConsentUpdateItem[],
): Promise<ConsentsResponse> {
  const url = `${API_BASE_URL}/api/v1/users/me/consents`;
  const res = await authFetch(url, {
    method: 'PATCH',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify({ consents: items }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Consent update failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Users Management (앱 관리)                                           */
/* ------------------------------------------------------------------ */

export interface AppUserItem {
  user_id: string;
  email: string | null;
  name: string | null;
  picture: string | null;
  role: string;
  plan: string;
  status: string;
  created_at: string | null;
}

export interface UsersListResponse {
  users: AppUserItem[];
  total: number;
}

export interface RoleUpdateResponse {
  success: boolean;
  user_id: string;
  role: string;
  message: string;
}

/** 사용자 목록 조회 (검색 · 역할 필터 · 페이지네이션) */
export async function listUsers(params: {
  q?: string;
  role?: string;
  skip?: number;
  limit?: number;
}): Promise<UsersListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.role && params.role !== 'all') qs.set('role', params.role);
  if (params.skip !== undefined) qs.set('skip', String(params.skip));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const qsStr = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/users${qsStr}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`List users failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 사용자 역할 변경 */
export async function updateUserRole(
  userId: string,
  role: string,
): Promise<RoleUpdateResponse> {
  const url = `${API_BASE_URL}/api/v1/users/${encodeURIComponent(userId)}/role`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Update user role failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface AdminPlanUpdateResponse {
  success: boolean;
  user_id: string;
  plan: string;
  message: string;
}

/** 사용자 플랜 직접 변경 (owner 전용, 결제 우회) */
export async function adminUpdateUserPlan(
  userId: string,
  plan: string,
): Promise<AdminPlanUpdateResponse> {
  const url = `${API_BASE_URL}/api/v1/users/${encodeURIComponent(userId)}/plan`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Update user plan failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface CountryTokenUsage {
  country: string;
  tokens: number;
  user_count: number;
}

export interface DailyUsageStatsResponse {
  date: string;
  total_tokens: number;
  by_country: CountryTokenUsage[];
  chat_events: number;
by_feature?: FeatureEventCount[];

}

/** 일별 토큰 사용량 통계 (owner 전용) */
export async function getDailyUsageStats(date: string): Promise<DailyUsageStatsResponse> {
  const qs = new URLSearchParams({ date });
  const url = `${API_BASE_URL}/api/v1/users/stats/daily-usage?${qs.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Get daily usage stats failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 이메일로 사용자 검색 (팀 멤버 추가용) */
export interface UserSearchItem {
  user_id: string;
  email: string | null;
  name: string | null;
  picture: string | null;
}

export async function searchUsersByEmail(
  q: string,
  limit: number = 20,
): Promise<{ users: UserSearchItem[] }> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  const url = `${API_BASE_URL}/api/v1/users/search?${qs.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`User search failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Teams                                                               */
/* ------------------------------------------------------------------ */

export interface TeamInfo {
  team_id: string;
  name: string;
  description: string;
  owner_id: string;
  tokens_total: number;
  tokens_used: number;
  storage_total_bytes: number;
  storage_used_bytes: number;
  status: string;
  member_count: number;
  created_at: string | null;
  my_tokens_allocated?: number | null;
  my_tokens_used?: number | null;
  my_role?: string | null;
next_token_expiry?: string | null;
charge_expiry_preview?: string | null;

}

export interface TeamMember {
  team_id: string;
  user_id: string;
  email: string | null;
  name: string | null;
  picture: string | null;
  role: string;
  tokens_allocated: number;
  tokens_used: number;
  joined_at: string | null;
}

export interface TeamDetailResponse {
  team: TeamInfo;
  members: TeamMember[];
}

export interface TeamMembershipEvent {
  event_id: string;
  team_id: string;
  team_name: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  member_role: string;
  action: string;
  reason: string;
  actor_user_id: string;
  actor_name: string | null;
  status: string;
  reclaimed_tokens: number;
  left_workspace_group_count: number;
  left_dm_conversation_count: number;
  reassigned_team_item_count: number;
  reassigned_team_file_count: number;
  reassigned_dm_file_count: number;
  deleted_dm_file_count: number;
  error_message: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface LeaveTeamResponse {
  success: boolean;
  team_id: string;
  team_name: string;
  leave_event_id: string;
  reclaimed_tokens: number;
  left_workspace_group_count: number;
  left_dm_conversation_count: number;
  reassigned_team_item_count: number;
  reassigned_team_file_count: number;
  reassigned_dm_file_count: number;
  deleted_dm_file_count: number;
  message: string;
}

export interface RemoveTeamMemberResponse {
  success: boolean;
  team_id: string;
  team_name: string;
  user_id: string;
  membership_event_id: string;
  reclaimed_tokens: number;
  left_workspace_group_count: number;
  left_dm_conversation_count: number;
  reassigned_team_item_count: number;
  reassigned_team_file_count: number;
  reassigned_dm_file_count: number;
  deleted_dm_file_count: number;
  message: string;
}

export interface TeamInvitationResponse {
  invitation_id: string;
  team_id: string;
  inviter_id: string;
  invitee_id: string;
  invitee_email: string | null;
  invitee_name: string | null;
  inviter_name: string | null;
  conversation_id: string;
  message_id: string;
  status: string;
  created_at: string | null;
  responded_at: string | null;
}

export interface TeamInviteLink {
  invite_link_id: string;
  team_id: string;
  status: string;
  created_by: string;
  created_by_name: string | null;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  created_at: string | null;
  invite_path: string | null;
  invite_token: string | null;
}

export interface TeamInviteLinkPreview {
  team_id: string;
  team_name: string;
  inviter_name: string | null;
  remaining_uses: number;
  expires_at: string | null;
}

export interface TeamInviteLinkAcceptResponse {
  success: boolean;
  team_id: string;
  team_name: string;
  already_member: boolean;
}

export interface TeamInvitationAcceptResponse {
  success: boolean;
  team_id: string;
  team_name: string;
  already_member: boolean;
}

function parseApiErrorDetail(raw: string): string {
  const text = raw.trim();
  let detail = text;

  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text) as { detail?: unknown; message?: unknown };
      if (parsed && typeof parsed === 'object') {
        const candidate = parsed.detail ?? parsed.message;
        if (typeof candidate === 'string' && candidate.trim()) {
          detail = candidate.trim();
        }
      }
    } catch {
      // JSON 파싱 실패 시 원문 유지
    }
  }

  return detail;
}

function normalizeTeamSpaceLimitMessage(detail: string): string {
  if (/현재 플랜에서는 팀 스페이스를 최대/.test(detail)) {
    const m = detail.match(/최대\s*(\d+)\s*개/);
    const maxCount = m?.[1];
    if (maxCount) {
      return `팀 스페이스 한도에 도달했어요. 현재 플랜에서는 최대 ${maxCount}개(소유/참여 합산)까지 참여할 수 있어요.`;
    }
    return '팀 스페이스 한도에 도달했어요. 기존 팀에서 나가거나 플랜을 변경해 주세요.';
  }

  return '';
}

function normalizeTeamApiErrorMessage(raw: string, fallback: string): string {
  const detail = parseApiErrorDetail(raw);
  const limitMessage = normalizeTeamSpaceLimitMessage(detail);
  return limitMessage || detail || fallback;
}

/** 팀 생성 */
export async function createTeam(params: {
  name: string;
  description?: string;
  tokens_total?: number;
  storage_total_bytes?: number;
}): Promise<TeamInfo> {
  const url = `${API_BASE_URL}/api/v1/teams`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `팀 생성에 실패했습니다. (${res.status})`)
      || `팀 생성에 실패했습니다. (${res.status})`,
    );
  }
  return res.json();
}

/** 내 팀 목록 */
export async function listMyTeams(): Promise<{ teams: TeamInfo[] }> {
  const url = `${API_BASE_URL}/api/v1/teams`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`List teams failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀 상세 (멤버 포함) */
export async function getTeamDetail(teamId: string): Promise<TeamDetailResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Get team detail failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀 정보 수정 */
export async function updateTeam(
  teamId: string,
  params: { name?: string; description?: string; tokens_total?: number; storage_total_bytes?: number },
): Promise<TeamInfo> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Update team failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀에 멤버 초대 (이메일) — 초대 레코드 생성 + DM 전송 */
export async function addTeamMember(
  teamId: string,
  email: string,
): Promise<TeamInvitationResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/members`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `멤버 추가에 실패했습니다. (${res.status})`),
    );
  }
  return res.json();
}

/** 팀 멤버 제거 */
export async function removeTeamMember(
  teamId: string,
  userId: string,
): Promise<RemoveTeamMemberResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `멤버 제거에 실패했습니다. (${res.status})`),
    );
  }
  return res.json();
}

/** 팀 자발적 탈퇴 */
export async function leaveTeam(
  teamId: string,
): Promise<LeaveTeamResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/leave`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `팀 나가기에 실패했습니다. (${res.status})`),
    );
  }
  return res.json();
}

/** 팀원 자발적 탈퇴 이력 */
export async function listTeamLeaveLogs(
  teamId: string,
  limit: number = 20,
): Promise<{ logs: TeamMembershipEvent[] }> {
  const qs = new URLSearchParams({ limit: String(limit) });
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/leave-logs?${qs.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `탈퇴 이력 조회에 실패했습니다. (${res.status})`),
    );
  }
  return res.json();
}

/** 팀 삭제 (owner만 가능) */
export async function deleteTeam(teamId: string): Promise<{ success: boolean }> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Delete team failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 멤버 토큰 배분 */
export async function allocateTokens(
  teamId: string,
  userId: string,
  tokensAllocated: number,
): Promise<TeamMember> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/tokens`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ tokens_allocated: tokensAllocated }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Allocate tokens failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 멤버 미사용 배분 토큰 회수 */
export async function reclaimMemberTokens(
  teamId: string,
  userId: string,
  tokensToReclaim: number,
): Promise<TeamMember> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/tokens/reclaim`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ tokens_to_reclaim: tokensToReclaim }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Reclaim member tokens failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* =====================================================================
 * 팀 초대 API
 * ===================================================================== */

/** 팀 초대 목록 */
export async function listTeamInvitations(
  teamId: string,
): Promise<{ invitations: TeamInvitationResponse[] }> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/invitations`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`List invitations failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 초대 수락 */
export async function acceptInvitation(
  invitationId: string,
): Promise<TeamInvitationAcceptResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/invitations/${encodeURIComponent(invitationId)}/accept`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `초대 수락에 실패했습니다. (${res.status})`),
    );
  }
  return res.json();
}

/** 초대 거절 */
export async function declineInvitation(
  invitationId: string,
): Promise<{ success: boolean }> {
  const url = `${API_BASE_URL}/api/v1/teams/invitations/${encodeURIComponent(invitationId)}/decline`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Decline invitation failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 초대 다시 보내기 */
export async function resendInvitation(
  teamId: string,
  inviteeId: string,
): Promise<TeamInvitationResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/invitations/${encodeURIComponent(inviteeId)}/resend`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Resend invitation failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀 공용 초대 링크 목록 */
export async function listTeamInviteLinks(
  teamId: string,
  status?: string,
): Promise<{ links: TeamInviteLink[] }> {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  const qsStr = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/invite-links${qsStr}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`List invite links failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀 공용 초대 링크 생성 */
export async function createTeamInviteLink(
  teamId: string,
  params: { expires_hours?: number; max_uses?: number } = {},
): Promise<TeamInviteLink> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/invite-links`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Create invite link failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀 공용 초대 링크 비활성화 */
export async function deactivateTeamInviteLink(
  teamId: string,
  inviteToken: string,
): Promise<TeamInviteLink> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/invite-links/${encodeURIComponent(inviteToken)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Deactivate invite link failed (${res.status}): ${text}`);
  }
  return res.json();
}

/** 팀 공용 초대 링크 미리보기 (비회원 포함) */
export async function getTeamInviteLinkPreview(
  inviteToken: string,
): Promise<TeamInviteLinkPreview> {
  const url = `${API_BASE_URL}/api/v1/teams/invite-links/${encodeURIComponent(inviteToken)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Invite preview failed (${res.status}): ${text}`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Skill Management                                                    */
/* ------------------------------------------------------------------ */

export interface SkillSummary {
  skill_id: string;
  name: string;
  description: string;
  body_preview?: string;
  created_at: string;
  file_size: number;
  source_filename: string;
  has_references: boolean;
  has_assets: boolean;
  has_scripts: boolean;
  /** 사내 공유 여부. 서버가 아직 컬럼을 모르는 구버전이면 비어 있다. */
  is_shared?: boolean;
  /** 공유 시각(ISO). 공유 전이면 빈 문자열. */
  shared_at?: string;
}

/** 사내에 공유된 스킬 — 내 스킬 요약에 업로더 정보가 붙는다. */
export interface SharedSkillSummary extends SkillSummary {
  owner_user_id: string;
  owner_name: string | null;
  owner_department: string | null;
  /** 호출자가 업로더 본인인지. */
  is_mine: boolean;
}

export interface SharedSkillListResult {
  skills: SharedSkillSummary[];
  total: number;
}

export interface SkillShareResult {
  skill_id: string;
  is_shared: boolean;
  shared_at: string;
}

export interface SkillUploadResult {
  status: string;
  skills: SkillSummary[];
  errors: string[];
}

export interface SkillListResult {
  skills: SkillSummary[];
  total: number;
}

export async function uploadSkill(files: File[]): Promise<SkillUploadResult> {
  const errors = files.flatMap((file) => {
    if (!/\.(md|zip)$/i.test(file.name)) return [`${file.name} — .md, .zip 파일만 업로드할 수 있습니다.`];
    if (!file.size) return [`${file.name} — 빈 파일은 업로드할 수 없습니다.`];
    if (file.size > 16 * 1024 * 1024) return [`${file.name} — 파일 크기는 16MB 이하여야 합니다.`];
    return [];
  });
  if (!files.length) errors.push('업로드할 스킬 파일을 선택하세요.');
  if (errors.length) throw new Error(errors.join('\n'));

  const form = new FormData();
  for (const f of files) form.append('files', f);

  const post = async (action: 'validate' | 'upload') => {
    const res = await authFetch(`${API_BASE_URL}/api/v1/skills/${action}`, {
      method: 'POST', headers: getHeaders(false), body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = body?.detail;
      const message = Array.isArray(detail?.errors)
        ? detail.errors.join('\n')
        : typeof detail === 'string' ? detail : detail?.message;
      throw new Error(message || `스킬 업로드 실패 (${res.status})`);
    }
    return res;
  };
  await post('validate');
  return (await post('upload')).json();
}

export async function listSkills(): Promise<SkillListResult> {
  const url = `${API_BASE_URL}/api/v1/skills`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Skill list failed (${res.status})`);
  return res.json();
}

/** 사내에 공유된 스킬 목록 (테넌트 전체, 최근 공유 순). */
export async function listSharedSkills(): Promise<SharedSkillListResult> {
  const url = `${API_BASE_URL}/api/v1/skills/shared`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Shared skill list failed (${res.status})`);
  return res.json();
}

async function skillShareRequest(skillId: string, method: 'POST' | 'DELETE'): Promise<SkillShareResult> {
  const url = `${API_BASE_URL}/api/v1/skills/${encodeURIComponent(skillId)}/share`;
  const res = await authFetch(url, { method, headers: getHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(typeof body?.detail === 'string' ? body.detail : `Skill share update failed (${res.status})`);
  }
  return res.json();
}

/** 내 스킬을 사내에 공유한다. */
export function shareSkill(skillId: string): Promise<SkillShareResult> {
  return skillShareRequest(skillId, 'POST');
}

/** 사내 공유를 취소한다. */
export function unshareSkill(skillId: string): Promise<SkillShareResult> {
  return skillShareRequest(skillId, 'DELETE');
}

export type SkillSourceKind = 'user' | 'official' | 'shared';
export interface SkillSourceFile {
  path: string;
  content: string | null;
  binary: boolean;
  editable: boolean;
  sha256: string;
  size: number;
}

/* 'user' 는 내 스킬(편집 가능), 'official' / 'shared' 는 읽기 전용 경로. */
const SKILL_SOURCE_PREFIX: Record<SkillSourceKind, string> = { user: '', official: 'official/', shared: 'shared/' };

function skillSourceUrl(kind: SkillSourceKind, skillId: string): string {
  return `${API_BASE_URL}/api/v1/skills/${SKILL_SOURCE_PREFIX[kind]}${encodeURIComponent(skillId)}/source`;
}

async function sourceResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(typeof body?.detail === 'string' ? body.detail : `Skill file request failed (${response.status})`);
  }
  return response.json();
}

export async function listSkillSourceFiles(kind: SkillSourceKind, skillId: string): Promise<{ files: string[] }> {
  return sourceResponse(await authFetch(skillSourceUrl(kind, skillId), { headers: getHeaders() }));
}

export async function getSkillSourceFile(kind: SkillSourceKind, skillId: string, path: string): Promise<SkillSourceFile> {
  return sourceResponse(await authFetch(`${skillSourceUrl(kind, skillId)}/file?${new URLSearchParams({ path })}`, { headers: getHeaders() }));
}

export async function saveSkillSourceFile(skillId: string, file: Pick<SkillSourceFile, 'path' | 'content' | 'sha256'>): Promise<SkillSourceFile> {
  return sourceResponse(await authFetch(`${skillSourceUrl('user', skillId)}/file`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(file),
  }));
}

export async function renameSkill(skillId: string, newName: string): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/skills/${encodeURIComponent(skillId)}/name`;
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ name: newName }),
  });
  if (!res.ok) throw new Error(`Skill rename failed (${res.status})`);
}

export async function deleteSkill(skillId: string): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/skills/${encodeURIComponent(skillId)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Skill delete failed (${res.status})`);
}

export async function getSkillFavorites(): Promise<string[]> {
  const url = `${API_BASE_URL}/api/v1/skills/favorites`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  return data.skill_ids ?? [];
}

export async function setSkillFavorites(skillIds: string[]): Promise<string[]> {
  const url = `${API_BASE_URL}/api/v1/skills/favorites`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ skill_ids: skillIds }),
  });
  if (!res.ok) throw new Error(`Skill favorites update failed (${res.status})`);
  const data = await res.json();
  return data.skill_ids ?? [];
}

/* ------------------------------------------------------------------ */
/* Official Skill API                                                  */
/* ------------------------------------------------------------------ */

export type OutputFormat = 'auto' | 'text' | 'image' | 'video' | 'docx' | 'pptx' | 'hwpx' | 'xlsx' | 'interactive_ui';

export interface OfficialSkillSummary {
  skill_id: string;
  name: string;
  description: string;
  category: string;
  format: 'md' | 'zip';
  output_format: OutputFormat;
  status: 'active' | 'draft';
  creator_id: string;
  has_references: boolean;
  has_assets: boolean;
  has_scripts: boolean;
  reference_keys: string[];
  asset_keys: string[];
  script_keys: string[];
  display_name?: Record<string, string>;
  display_description?: Record<string, string>;
  creator_name?: string;
  use_cases?: Record<string, string[]>;
  created_at: string;
  updated_at: string;
has_thumbnail?: boolean;
thumbnail_focus?: { x: number; y: number };
thumbnail_version?: number;

}

export interface OfficialSkillListResult {
  skills: OfficialSkillSummary[];
  total: number;
}

export async function listOfficialSkills(category?: string): Promise<OfficialSkillListResult> {
  const params = category ? `?category=${encodeURIComponent(category)}` : '';
  const url = `${API_BASE_URL}/api/v1/skills/official${params}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Official skill list failed (${res.status})`);
  return res.json();
}

export async function listAllOfficialSkills(): Promise<OfficialSkillListResult> {
  const url = `${API_BASE_URL}/api/v1/skills/official/all`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Official skill list failed (${res.status})`);
  return res.json();
}

export async function getOfficialSkill(skillId: string): Promise<OfficialSkillSummary> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Official skill get failed (${res.status})`);
  return res.json();
}

export async function getOfficialSkillContent(skillId: string): Promise<string> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/content`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Official skill content failed (${res.status})`);
  const data = await res.json();
  return data.content ?? '';
}

export async function createOfficialSkill(data: {
  name: string;
  description: string;
  category: string;
  format: 'md' | 'zip';
  output_format?: OutputFormat;
  content: string;
  status?: 'draft' | 'active';
}): Promise<OfficialSkillSummary> {
  const url = `${API_BASE_URL}/api/v1/skills/official`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Official skill create failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function updateOfficialSkill(
  skillId: string,
  data: Partial<{
    name: string;
    description: string;
    category: string;
    output_format: OutputFormat;
    content: string;
    status: 'draft' | 'active';
  }>,
): Promise<OfficialSkillSummary> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}`;
  const res = await authFetch(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Official skill update failed (${res.status})`);
  return res.json();
}

export async function deleteOfficialSkill(skillId: string): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Official skill delete failed (${res.status})`);
}

export async function updateOfficialSkillStatus(
  skillId: string,
  status: 'draft' | 'active',
): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/status`;
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(`Official skill status update failed (${res.status})`);
}

export async function listOfficialSkillFiles(skillId: string): Promise<{
  reference_keys: string[];
  asset_keys: string[];
  script_keys: string[];
}> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/files`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Official skill files list failed (${res.status})`);
  return res.json();
}

export async function uploadOfficialSkillFile(
  skillId: string,
  directory: 'references' | 'assets' | 'scripts',
  file: File,
): Promise<{ file_path: string }> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/files`;
  const form = new FormData();
  form.append('directory', directory);
  form.append('file', file);

  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(false),
    body: form,
  });
  if (!res.ok) throw new Error(`Official skill file upload failed (${res.status})`);
  return res.json();
}

export async function createOfficialSkillInlineFile(
  skillId: string,
  directory: 'references' | 'scripts',
  filename: string,
  content: string,
): Promise<{ file_path: string }> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/files/inline`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ directory, filename, content }),
  });
  if (!res.ok) throw new Error(`Official skill inline file create failed (${res.status})`);
  return res.json();
}

export async function deleteOfficialSkillFile(
  skillId: string,
  directory: string,
  filename: string,
): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/files/${directory}/${encodeURIComponent(filename)}`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Official skill file delete failed (${res.status})`);
}

export async function getOfficialSkillFileContent(
  skillId: string,
  directory: string,
  filename: string,
): Promise<{ content: string | null; binary: boolean }> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/files/${directory}/${encodeURIComponent(filename)}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Official skill file content failed (${res.status})`);
  return res.json();
}

/** 팀 공용 초대 링크 수락 */
export async function acceptTeamInviteLink(
  inviteToken: string,
): Promise<TeamInviteLinkAcceptResponse> {
  const url = `${API_BASE_URL}/api/v1/teams/invite-links/${encodeURIComponent(inviteToken)}/accept`;
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      normalizeTeamApiErrorMessage(text, `초대 링크 수락에 실패했습니다. (${res.status})`),
    );
  }
  return res.json();
}

// Restored B2C API and type contracts.
export function buildGoogleLoginUrl(frontendRedirectUri?: string): string {
  const params = new URLSearchParams();
  if (frontendRedirectUri) params.set('redirect_uri', frontendRedirectUri);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return `${API_BASE_URL}/api/v1/auth/google/login${qs}`;
}

export interface FeatureEventCount {
  /** workspace_type: agent / live-agent / doc-agent / cad-material / ... */
  feature: string;
  count: number;
  tokens: number;
}

export interface UserActivityItem {
  created_at: string | null;
  feature: string;
  status: string; // completed / failed / unknown
  tokens: number;
  message_id?: string | null;
  session_id?: string | null;
  item_id?: string | null;
  usage_seq?: number;
}

export interface UserActivityResponse {
  user_id: string;
  items: UserActivityItem[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export async function getUserActivity(
  userId: string,
  params: {
    limit?: number;
    offset?: number;
    dateFrom?: string;
    dateTo?: string;
    feature?: string;
    status?: string;
  } = {},
): Promise<UserActivityResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.dateFrom) qs.set('date_from', params.dateFrom);
  if (params.dateTo) qs.set('date_to', params.dateTo);
  if (params.feature) qs.set('feature', params.feature);
  if (params.status) qs.set('status', params.status);
  const qsStr = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/users/${encodeURIComponent(userId)}/activity${qsStr}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Get user activity failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface RecentActivityItem {
  created_at: string | null;
  user_id: string;
  name?: string | null;
  email?: string | null;
  feature: string;
  status: string; // completed / failed / cancelled / in_progress
  tokens: number;
  message_id?: string | null;
  session_id?: string | null;
  item_id?: string | null;
}

export interface RecentActivityResponse {
  items: RecentActivityItem[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export async function getRecentActivity(
  params: {
    limit?: number;
    offset?: number;
    dateFrom?: string;
    dateTo?: string;
    feature?: string;
    status?: string;
    q?: string;
  } = {},
): Promise<RecentActivityResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.dateFrom) qs.set('date_from', params.dateFrom);
  if (params.dateTo) qs.set('date_to', params.dateTo);
  if (params.feature) qs.set('feature', params.feature);
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  const qsStr = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/users/activity/recent${qsStr}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Get recent activity failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface GatedMessageItem {
  message_id?: string | null;
  role: string;
  content: string;
  created_at?: string | null;
}

export interface UserActivityMessagesResponse {
  user_id: string;
  item_id: string;
  messages: GatedMessageItem[];
}

export async function getUserActivityMessages(
  userId: string,
  itemId: string,
  params: { limit?: number; messageId?: string } = {},
): Promise<UserActivityMessagesResponse> {
  const qs = new URLSearchParams({ item_id: itemId });
  if (params.messageId) qs.set('message_id', params.messageId);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const url = `${API_BASE_URL}/api/v1/users/${encodeURIComponent(userId)}/activity/messages?${qs.toString()}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Get user messages failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface ErrorEventItem {
  error_id: string;
  user_id?: string | null;
  feature?: string | null;
  source?: string | null;
  error_type?: string | null;
  message?: string | null;
  detail?: string | null;
  http_status?: number | null;
  path?: string | null;
  session_id?: string | null;
  env?: string | null;
  created_at?: string | null;
}

export interface ErrorEventsResponse {
  items: ErrorEventItem[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export async function listErrorEvents(
  params: { limit?: number; offset?: number; feature?: string; errorUserId?: string } = {},
): Promise<ErrorEventsResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.feature) qs.set('feature', params.feature);
  if (params.errorUserId) qs.set('error_user_id', params.errorUserId);
  const qsStr = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/users/errors${qsStr}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`List error events failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface OverviewSeriesPoint {
  date: string;
  active_users: number;
  events: number;
  tokens: number;
}

export interface OverviewAnalyticsResponse {
  from_date: string;
  to_date: string;
  total_tokens: number;
  total_events: number;
  active_users: number;
  error_count: number;
  avg_events_per_user?: number;
  dau?: number;
  wau?: number;
  mau?: number;
  by_feature?: FeatureEventCount[];
  series?: OverviewSeriesPoint[];
}

export async function getOverviewAnalytics(
  params: { dateFrom?: string; dateTo?: string } = {},
): Promise<OverviewAnalyticsResponse> {
  const qs = new URLSearchParams();
  if (params.dateFrom) qs.set('date_from', params.dateFrom);
  if (params.dateTo) qs.set('date_to', params.dateTo);
  const qsStr = qs.toString() ? `?${qs.toString()}` : '';
  const url = `${API_BASE_URL}/api/v1/users/analytics/overview${qsStr}`;
  const res = await authFetch(url, { headers: getHeaders() });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Get overview analytics failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function deleteTeamInviteLink(
  teamId: string,
  inviteToken: string,
): Promise<{ success: boolean }> {
  const url = `${API_BASE_URL}/api/v1/teams/${encodeURIComponent(teamId)}/invite-links/${encodeURIComponent(inviteToken)}/permanent`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Delete invite link failed (${res.status}): ${text}`);
  }
  return res.json();
}

export interface OfficialSkillThumbnailResult {
  skill_id: string;
  has_thumbnail: boolean;
  thumbnail_version: number;
  thumbnail_focus: { x: number; y: number };
}

export async function uploadOfficialSkillThumbnail(
  skillId: string,
  file: File,
): Promise<OfficialSkillThumbnailResult> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/thumbnail`;
  const form = new FormData();
  form.append('file', file);
  const res = await authFetch(url, {
    method: 'POST',
    headers: getHeaders(false),
    body: form,
  });
  if (!res.ok) throw new Error(`Official skill thumbnail upload failed (${res.status})`);
  return res.json();
}

export async function updateOfficialSkillThumbnailFocus(
  skillId: string,
  focus: { x: number; y: number },
): Promise<{ skill_id: string; thumbnail_focus: { x: number; y: number } }> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/thumbnail-focus`;
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(focus),
  });
  if (!res.ok) throw new Error(`Official skill thumbnail focus update failed (${res.status})`);
  return res.json();
}

export async function deleteOfficialSkillThumbnail(skillId: string): Promise<void> {
  const url = `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/thumbnail`;
  const res = await authFetch(url, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`Official skill thumbnail delete failed (${res.status})`);
}

export function getOfficialSkillThumbnailUrl(skillId: string, version?: number): string {
  const v = version ? `?v=${version}` : '';
  return `${API_BASE_URL}/api/v1/skills/official/${encodeURIComponent(skillId)}/thumbnail${v}`;
}
